import React, { Dispatch, ReactNode, SetStateAction, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Entity } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { EditDocumentInput, GetDocumentUrlInput } from '@amzn/coral_com-amazon-musicpaperrecordkeeperservice';
import approvalsTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import contractTokens from '@amzn/meridian-tokens/base/icon/document';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import MeridianLink from '@amzn/meridian/link';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { IDetailCellRendererParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { History } from 'history';
import { isNil } from 'lodash';
import { Link } from 'react-router-dom';
import { RECORD_KEEPER_TENANT_ID, recordKeeperClient } from 'src/api/recordKeeper/RecordKeeperClient';
import { DocumentDetail } from 'src/components/Agreements/DocumentDetail';
import { EditDocumentDetail } from 'src/components/Agreements/DocumentDetail/EditDocumentDetail';
import { ViewDocumentDetail } from 'src/components/Agreements/DocumentDetail/ViewDocumentDetail';
import { ServiceTag } from 'src/components/Agreements/Shared';
import { EntityLink } from 'src/components/Agreements/Shared';
import { LINK_COLOR } from 'src/constants/agGrid';
import { AppContext } from 'src/contexts/appContext';

import './cell-renderers.scss';
import { DocumentRow } from './grid-types';

type RendererProps<T extends keyof DocumentRow> = CustomCellRendererProps<DocumentRow, DocumentRow[T]>;
type AgreementsRenderer<T extends keyof DocumentRow> = (props: RendererProps<T>) => ReactNode;

export function LinksRenderer(): AgreementsRenderer<'links'> {
    const LinksCell = (props: RendererProps<'links'>) => (
        <Row alignmentVertical={'center'} height={'100%'}>
            {Object.keys(props.value ?? {}).map((name) => {
                return renderLink(name, props.value?.[name] ?? '');
            })}
        </Row>
    );
    LinksCell.displayName = 'LinksCell';
    return LinksCell;
}

export function LicensorsRenderer(): AgreementsRenderer<'licensors'> {
    const LicensorsCell = (props: RendererProps<'licensors'>) => {
        if (Array.isArray(props.value)) {
            return (
                <Row alignmentVertical={'center'} height={'100%'}>
                    {props.value.map((v) => (
                        <EntityLink name={v} key={v} />
                    ))}
                </Row>
            );
        } else {
            return props.value;
        }
    };
    LicensorsCell.displayName = 'LicensorsCell';
    return LicensorsCell;
}

interface IFileNameComponent {
    linkText: string | null | undefined;
    row: DocumentRow | undefined;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setErrorMessage: Dispatch<SetStateAction<string | undefined>>;
}

const FileNameComponent: React.FC<IFileNameComponent> = (props) => {
    const { setLoading, setErrorMessage } = props;
    const { setRightSideBarContent, setIsRightSideBarOpen } = useContext(AppContext);

    const onClick = useCallback(() => {
        if (isNil(props.row) || isNil(props.linkText)) {
            return;
        }
        setRightSideBarContent({
            heading: props.row.name,
            omitHeading: true,
            mainBody: (
                <DocumentDetail
                    documentId={props.row.id}
                    tenantId={RECORD_KEEPER_TENANT_ID}
                    data={props.row}
                    setLoading={setLoading}
                    setErrorMessage={setErrorMessage}
                    onClose={() => setIsRightSideBarOpen(false)}
                />
            )
        });
        setIsRightSideBarOpen(true);
    }, [props.row, props.linkText, setLoading, setErrorMessage, setRightSideBarContent, setIsRightSideBarOpen]);

    return (
        <Link to={'#'} style={{ color: LINK_COLOR, textDecoration: 'none' }} onClick={onClick}>
            {props.linkText}
        </Link>
    );
};

export function FileNameRenderer(
    setLoading: Dispatch<SetStateAction<boolean>>,
    setErrorMessage: Dispatch<SetStateAction<string | undefined>>
): AgreementsRenderer<'name'> {
    const FileNameCell = (props: RendererProps<'name'>) => {
        return (
            <FileNameComponent
                linkText={props.value}
                row={props.data}
                setLoading={setLoading}
                setErrorMessage={setErrorMessage}
            />
        );
    };
    FileNameCell.displayName = 'FileNameCell';
    return FileNameCell;
}

export function ServicesRenderer(): AgreementsRenderer<'services'> {
    const ServicesCell = (props: RendererProps<'services'>) => {
        return (
            <Row alignmentVertical={'center'} height={'100%'}>
                {props.value?.map((v) => (
                    <ServiceTag name={v} key={v} />
                ))}
            </Row>
        );
    };
    ServicesCell.displayName = 'ServicesCell';
    return ServicesCell;
}

const DocumentTokens: { [name: string]: ReactNode } = {
    ['Contract Central']: <Icon tokens={contractTokens} data-testid={'contract-central-icon'} />,
    ['Approvals']: <Icon tokens={approvalsTokens} data-testid={'approvals-icon'} />
};

function renderLink(name: string, href: string) {
    return (
        <Tooltip title={name} position={'top'} key={name}>
            <Button type={'link'} href={href} size={'small'} target={'_blank'}>
                {DocumentTokens[name] ?? <Icon tokens={contractTokens} />}
            </Button>
        </Tooltip>
    );
}

export function SearchHitsRenderer(params: IDetailCellRendererParams<DocumentRow>) {
    return (
        <Row>
            <Column spacingInset={'300 none 300 600'}>
                {params.data?.searchHits?.map((match, idx) => (
                    <SearchHit score={match.score} text={match.match} key={idx} />
                ))}
            </Column>
        </Row>
    );
}

const SearchHit: React.FC<{ score: number; text: string }> = (props) => {
    const [open, setOpen] = useState(false);
    const cleanedText = useMemo(() => {
        // these are added during plain text extraction, we don't need to show the user
        return props.text.replaceAll(/=== START OF PAGE \d+ ===/g, '').replaceAll(/=== END OF PAGE \d+ ===/g, '');
    }, [props.text]);

    const score = `Relevance: ${(100 * props.score).toFixed(0)}%`;

    /* Doing some styling stuff here:
     * <Text /> is on the outside so that "em" spacing works properly
     * outer <Box /> sets the width and slightly insets the contents to space the "show more" correctly
     * nested <Box /> insets the document text even more for readability
     * inner <Box /> is used to achieve a precise "2lh" height when collapsed
     */
    return (
        <Text type={'b200'}>
            <Box width={'75em'} spacingInset={'200 300 200 300'} type={'outline'}>
                <Row alignmentHorizontal={'end'}>
                    <Text type={'b100'} color={'secondary'}>
                        {score}
                    </Text>
                </Row>
                <Column spacing={'0'}>
                    <Box spacingInset={'300 300 none 300'}>
                        <Box height={open ? undefined : '2lh'} overflowY={'hidden'}>
                            <p style={{ whiteSpace: 'wrap', margin: 0 }}>{cleanedText}</p>
                        </Box>
                    </Box>
                    <Row alignmentHorizontal={'end'}>
                        <MeridianLink onClick={() => setOpen((prev) => !prev)}>
                            {open ? 'Show less' : 'Show more'}
                        </MeridianLink>
                    </Row>
                </Column>
            </Box>
        </Text>
    );
};
